import React, { useEffect, useMemo } from 'react'
import { Box, Button, Center, IconButton, Spinner, VStack } from '@chakra-ui/react'
import { DeleteIcon } from '@chakra-ui/icons'
import { Field, Form, Formik } from 'formik'
import InfiniteScroll from 'react-infinite-scroll-component'
import * as Yup from 'yup'
import {
  useCreateSsoUser,
  useDeleteSsoUser,
  useFindAllSsoUsersForTenant,
  useUpdateSsoUser,
} from 'src/api/queries/cqgaccounts'
import { TextField as FormikTextField, Select as FormikSelect } from 'src/ui/formik'
import { Dialog, FormDialog, Placeholders, Row, Table2, useTable } from 'src/ui'
import { useCompanyContext } from 'src/companies/routes/company-context'
import { hasPermission } from 'src/utils/permissions'
import { useToast } from 'src/utils/toast'

const cqgExperienceOptions = [
  { value: 'bushel', label: 'Bushel' },
  { value: 'bushelone', label: 'Bushel One' },
]

const ssoUserSchema = Yup.object({
  username: Yup.string().trim().required('Required'),
  cqgExperience: Yup.string().oneOf(['bushel', 'bushelone']).required('Required'),
})

type FormDialogState = {
  mode: 'create' | 'edit'
  ssoUser?: SsoUser
}

function formatCqgExperience(cqgExperience: CqgExperience) {
  return cqgExperience === 'bushelone' ? 'Bushel One' : 'Bushel'
}

function SsoUserActions({
  row,
  onEdit,
  onDelete,
}: {
  row: { original: SsoUser }
  onEdit: (ssoUser: SsoUser) => void
  onDelete: (ssoUser: SsoUser) => void
}) {
  const canManage = hasPermission('sso_cqg_users_manage')

  return (
    <Row justifyContent="flex-end" alignItems="center">
      <Button
        size="sm"
        color="primary.500"
        variant="ghost"
        isDisabled={!canManage}
        onClick={() => onEdit(row.original)}
      >
        Edit
      </Button>
      <IconButton
        icon={<DeleteIcon />}
        aria-label={`Delete ${row.original.username}`}
        isRound
        variant="ghost"
        color="gray.600"
        isDisabled={!canManage}
        onClick={() => onDelete(row.original)}
      />
    </Row>
  )
}

function SsoUserFormDialog({
  isOpen,
  dialogState,
  isSubmitting,
  onClose,
  onSubmit,
}: {
  isOpen: boolean
  dialogState: FormDialogState | null
  isSubmitting: boolean
  onClose: () => void
  onSubmit: (values: { username: string; cqgExperience: CqgExperience }) => Promise<void>
}) {
  const ssoUser = dialogState?.ssoUser

  return (
    <FormDialog
      title={dialogState?.mode === 'edit' ? 'Edit SSO User' : 'Add SSO User'}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Formik
        enableReinitialize
        initialValues={{
          username: ssoUser?.username ?? '',
          cqgExperience: ssoUser?.cqgExperience ?? ('bushel' as CqgExperience),
        }}
        validationSchema={ssoUserSchema}
        onSubmit={async (values) => {
          await onSubmit(values)
        }}
      >
        {({ submitForm }) => (
          <Form>
            <FormDialog.Body>
              <Field
                name="username"
                label="Username"
                component={FormikTextField}
                formControlProps={{ height: '62px', mb: 4 }}
              />
              <Field
                name="cqgExperience"
                id="cqgExperience"
                label="Experience"
                component={FormikSelect}
                options={cqgExperienceOptions}
                formControlProps={{ height: '62px', mb: 0 }}
                selectProps={{ sortOptions: false }}
              />
            </FormDialog.Body>
            <FormDialog.Footer>
              <Button size="sm" variant="ghost" mr={2} onClick={onClose}>
                Cancel
              </Button>
              <Button size="sm" colorScheme="primary" isLoading={isSubmitting} onClick={submitForm}>
                Save
              </Button>
            </FormDialog.Footer>
          </Form>
        )}
      </Formik>
    </FormDialog>
  )
}

export default function SsoUsersIndex() {
  const { company } = useCompanyContext()
  const toast = useToast()
  const canManage = hasPermission('sso_cqg_users_manage')
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, error, status } =
    useFindAllSsoUsersForTenant()
  const { mutateAsync: createSsoUser } = useCreateSsoUser()
  const { mutateAsync: updateSsoUser } = useUpdateSsoUser()
  const { mutateAsync: deleteSsoUser } = useDeleteSsoUser()

  const [formDialog, setFormDialog] = React.useState<FormDialogState | null>(null)
  const [deleteDialog, setDeleteDialog] = React.useState<SsoUser | null>(null)
  const [isSaving, setIsSaving] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)

  const closeFormDialog = () => {
    if (isSaving) return
    setFormDialog(null)
  }

  const closeDeleteDialog = () => {
    if (isDeleting) return
    setDeleteDialog(null)
  }

  const handleSubmit = async (values: { username: string; cqgExperience: CqgExperience }) => {
    setIsSaving(true)

    try {
      if (formDialog?.mode === 'edit' && formDialog.ssoUser) {
        await updateSsoUser({
          tenantId: company.tenant_id,
          id: formDialog.ssoUser.id,
          username: values.username,
          cqgExperience: values.cqgExperience,
        })
        toast({ description: 'Successfully updated SSO user' })
      } else {
        await createSsoUser({
          tenantId: company.tenant_id,
          username: values.username,
          cqgExperience: values.cqgExperience,
        })
        toast({ description: 'Successfully created SSO user' })
      }

      setFormDialog(null)
    } catch {
      toast({
        status: 'error',
        description: `Failed to ${formDialog?.mode === 'edit' ? 'update' : 'create'} SSO user`,
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteDialog) return

    setIsDeleting(true)

    try {
      await deleteSsoUser({
        tenantId: company.tenant_id,
        id: deleteDialog.id,
      })
      toast({ description: 'Successfully deleted SSO user' })
      setDeleteDialog(null)
    } catch {
      toast({ status: 'error', description: 'Failed to delete SSO user' })
    } finally {
      setIsDeleting(false)
    }
  }

  const columns = useMemo(
    () => [
      {
        id: 'username',
        accessor: 'username',
        Header: 'Username',
        disableSortBy: true,
      },
      {
        id: 'cqgExperience',
        accessor: 'cqgExperience',
        Header: 'Experience',
        disableSortBy: true,
        Cell: ({ row }) => formatCqgExperience(row.original.cqgExperience),
      },
      {
        id: 'isExternallyAuthorized',
        accessor: 'isExternallyAuthorized',
        Header: 'Externally Authorized',
        disableSortBy: true,
        Cell: ({ row }) => (row.original.isExternallyAuthorized ? 'Yes' : 'No'),
      },
      {
        id: '_actions',
        accessor: '_actions',
        Header: '',
        disableSortBy: true,
        Cell: ({ row }) => (
          <SsoUserActions
            row={row}
            onEdit={(ssoUser) => setFormDialog({ mode: 'edit', ssoUser })}
            onDelete={setDeleteDialog}
          />
        ),
      },
    ],
    []
  )

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable({
    data,
    tableOptions: {
      initialState: {
        sortBy: [],
      },
    },
    columns,
  })

  useEffect(() => {
    if (
      !isFetching &&
      !error &&
      hasNextPage &&
      document.getElementById('sso_users_scroll_container').scrollHeight <= window.innerHeight
    ) {
      fetchNextPage()
    }
  }, [isFetching, hasNextPage, fetchNextPage, error])

  if (status === 'pending' && !isFetchingNextPage) {
    return <Placeholders.LoadingState mt={8} />
  }

  if (status === 'error') {
    return <Placeholders.FailedState mt={8} />
  }

  return (
    <>
      <VStack align="start">
        <Button
          alignSelf="end"
          size="sm"
          type="button"
          width="70px"
          colorScheme="primary"
          isDisabled={!canManage}
          onClick={() => setFormDialog({ mode: 'create' })}
        >
          Add
        </Button>
        <Box
          id="sso_users_scroll_container"
          overflowY="auto"
          height="calc(100vh - 180px)"
          width="100%"
        >
          {data.length === 0 ? (
            <Placeholders.EmptyState message="No SSO users found" />
          ) : (
            <Box bg="white" mb={4} pt={3} borderRadius={3} boxShadow="md">
              <InfiniteScroll
                next={fetchNextPage}
                hasMore={hasNextPage}
                loader={
                  <Center h="50px">
                    <Spinner speed="0.8s" thickness="3px" color="inherit" />
                  </Center>
                }
                dataLength={data?.length ?? 0}
                scrollableTarget="sso_users_scroll_container"
              >
                <Table2 {...getTableProps()} striped>
                  <Table2.Header headerGroups={headerGroups} />
                  <Table2.Body
                    rows={rows}
                    getTableBodyProps={getTableBodyProps}
                    prepareRow={prepareRow}
                  />
                </Table2>
              </InfiniteScroll>
            </Box>
          )}
        </Box>
      </VStack>

      <SsoUserFormDialog
        isOpen={!!formDialog}
        dialogState={formDialog}
        isSubmitting={isSaving}
        onClose={closeFormDialog}
        onSubmit={handleSubmit}
      />

      <Dialog
        title="Delete SSO User"
        isOpen={!!deleteDialog}
        onClose={closeDeleteDialog}
        actions={
          <>
            <Button size="sm" variant="ghost" mr={2} onClick={closeDeleteDialog}>
              Cancel
            </Button>
            <Button size="sm" colorScheme="primary" isLoading={isDeleting} onClick={handleDelete}>
              Delete
            </Button>
          </>
        }
      >
        <Box>
          <Box>Are you sure you want to delete this SSO user?</Box>
          {deleteDialog?.username && (
            <ul>
              <li>{deleteDialog.username}</li>
            </ul>
          )}
        </Box>
      </Dialog>
    </>
  )
}
